import { useEffect, useState } from "react";
import { Check, Copy, Eye, Link2, ShieldAlert } from "lucide-react";
import { ShareExpiry, SharedLink, sharesApi } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui-shim";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const EXPIRY_LABELS: Record<ShareExpiry, string> = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
  "never": "Never expires",
};

export function ShareDialog({ open, onClose, kind, targetId, title }: {
  open: boolean;
  onClose: () => void;
  kind: "outfit";
  targetId: string;
  title?: string;
}) {
  const [expiry, setExpiry] = useState<ShareExpiry>("30d");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<SharedLink | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) { setCreated(null); setExpiry("30d"); setCopied(false); }
  }, [open]);

  const shareUrl = created ? `${window.location.origin}/share/${created.token}` : "";

  async function create() {
    setCreating(true);
    try {
      const link = await sharesApi.create({ kind, target_id: targetId, title, expiry });
      setCreated(link);
    } catch (e: any) {
      toast.error(e?.message || "Could not create link.");
    } finally { setCreating(false); }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Select & copy manually.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="share-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Link2 className="w-5 h-5 text-oxblood" /> Share lookbook
          </DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Anyone with this link can view the lookbook. No login. No internal codes shown.
            </p>
            <div className="flex">
              <Input data-testid="share-link-output" readOnly value={shareUrl}
                className="rounded-none font-mono text-xs" />
              <Button onClick={copy} data-testid="share-copy-btn"
                className="rounded-none bg-foreground hover:bg-charcoal text-ivory">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            {created.expires_at && (
              <p className="text-[11px] text-muted-foreground">
                Expires {new Date(created.expires_at).toLocaleDateString()}.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              Revoke any time from Settings → Shared links.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Link expires</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="share-expiry-options">
                {(Object.keys(EXPIRY_LABELS) as ShareExpiry[]).map((e) => (
                  <button key={e} type="button" onClick={() => setExpiry(e)}
                    data-testid={`share-expiry-${e}`}
                    className={`px-3 py-2.5 text-sm border transition-colors ${
                      expiry === e ? "border-oxblood bg-oxblood/5 text-oxblood-deep" : "border-border hover:border-foreground/30"
                    }`}>
                    {EXPIRY_LABELS[e]}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Internal codes, retail values, and locations are hidden from the public view.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
              <Button onClick={create} disabled={creating}
                data-testid="share-create-btn"
                className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
                {creating ? "Creating…" : <>Create link <Eye className="w-4 h-4 ml-2" /></>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
